import { LegalPage } from "./LegalPage";

export function TermsPage() {
  return (
    <LegalPage
      attribution={
        <>
          Content adapted from{" "}
          <a href="https://github.com/Automattic/legalmattic" rel="noopener noreferrer" target="_blank">
            Automattic Legalmattic
          </a>{" "}
          under{" "}
          <a href="https://creativecommons.org/licenses/by-sa/4.0/" rel="noopener noreferrer" target="_blank">
            CC BY-SA 4.0
          </a>
          . Modified for Gaamed.
        </>
      }
      eyebrow="Legal"
      lastUpdated="July 17, 2026"
      title="TERMS OF SERVICE"
    >
      <h2>1. Acceptance of Terms</h2>
      <p>
        By accessing or using Gaamed ("the Service", "Platform"), you agree to be bound by
        these Terms of Service ("Terms"). If you do not agree to all of these Terms, do not
        access or use the Service.
      </p>
      <p>
        Gaamed is operated by Gaamed ("we", "us", "our"). We reserve the right to update
        these Terms at any time. Continued use of the Service after changes are posted
        constitutes acceptance of the revised Terms.
      </p>

      <h2>2. Description of Service</h2>
      <p>
        Gaamed is a gaming-venue management platform that connects players with gaming venues
        (gaming cafes, esports arenas, VR lounges and similar establishments). The platform
        provides:
      </p>
      <ul>
        <li>A mobile and web application for players to discover venues and book sessions.</li>
        <li>
          A provider portal for venue operators to manage bookings, sessions, team access and
          venue policies.
        </li>
        <li>An administrative console for platform management.</li>
      </ul>
      <p>
        We act as a technology intermediary. We do not own or operate any gaming venues listed
        on the platform.
      </p>

      <h2>3. User Accounts</h2>
      <h3>Registration</h3>
      <p>
        You must create an account to access core features. You agree to provide accurate,
        current and complete information and to keep your account credentials secure. You are
        responsible for all activity that occurs under your account.
      </p>
      <h3>Account types</h3>
      <ul>
        <li>
          <strong>Player accounts</strong> — for individuals who book gaming sessions.
        </li>
        <li>
          <strong>Provider accounts</strong> — for venue operators who list and manage venues.
          Provider registration is subject to approval by Gaamed.
        </li>
        <li>
          <strong>Admin accounts</strong> — for Gaamed staff only, not available for public
          registration.
        </li>
      </ul>
      <h3>Account termination</h3>
      <p>
        You may close your account at any time by contacting us. We reserve the right to
        suspend or terminate accounts that violate these Terms.
      </p>

      <h2>4. Booking Terms (Players)</h2>
      <p>
        When you make a booking through Gaamed, you are entering into an agreement with the
        venue operator, not with Gaamed. Gaamed facilitates the booking but is not a party to
        the session contract.
      </p>
      <ul>
        <li>
          Bookings are confirmed when you receive a booking confirmation with a QR check-in
          code.
        </li>
        <li>
          You must arrive at the venue at the booked time. Late arrivals may result in a
          reduced session duration at the venue's discretion.
        </li>
        <li>
          No-shows may be subject to a no-show penalty as set in the venue's policy, which is
          displayed before you confirm a booking.
        </li>
        <li>
          Cancellations are governed by the venue's cancellation policy. Please review the
          Refund Policy for details.
        </li>
      </ul>

      <h2>5. Provider Terms (Venue Operators)</h2>
      <p>By registering as a provider, you agree to:</p>
      <ul>
        <li>Provide accurate information about your venue, units and availability.</li>
        <li>
          Honour all confirmed bookings. Provider-initiated cancellations without valid cause
          may result in account suspension.
        </li>
        <li>
          Comply with all applicable laws, including consumer protection, health and safety,
          and data protection laws in your jurisdiction.
        </li>
        <li>
          Keep your venue policy (VAT rate, booking window, session limits, no-show penalty)
          up to date and clearly communicated to players.
        </li>
        <li>
          Not use the platform to offer services that are illegal or harmful.
        </li>
      </ul>
      <p>
        Gaamed reserves the right to remove any venue listing that violates these Terms or
        that receives consistent complaints from players.
      </p>

      <h2>6. Payments and Fees</h2>
      <p>
        Gaamed does not currently collect payments on behalf of venues. Payment for sessions
        is handled directly between players and venue operators according to the venue's own
        payment methods and pricing.
      </p>
      <p>
        We do not charge commission on bookings. Gaamed's business model is based on platform
        subscription fees for venue operators, where applicable.
      </p>
      <p>
        Venues are responsible for the collection and remittance of any applicable taxes,
        including VAT, in accordance with Egyptian law and any other applicable jurisdiction.
      </p>

      <h2>7. Prohibited Uses</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any unlawful purpose or in violation of these Terms.</li>
        <li>
          Attempt to gain unauthorised access to any part of the Service, or to any other
          systems connected to the Service.
        </li>
        <li>Transmit any malware, viruses or other harmful code.</li>
        <li>
          Scrape, crawl or otherwise harvest data from the Service without our prior written
          consent.
        </li>
        <li>
          Impersonate any person or entity, or misrepresent your affiliation with any person
          or entity.
        </li>
        <li>
          Post false, misleading or fraudulent venue information or reviews.
        </li>
        <li>
          Use the platform to facilitate transactions that are conducted outside of the
          platform's intended use in order to avoid any applicable fees.
        </li>
      </ul>

      <h2>8. Intellectual Property</h2>
      <p>
        The Service and its original content, features and functionality are and will remain
        the exclusive property of Gaamed and its licensors. You may not reproduce, distribute,
        modify, create derivative works of, publicly display or otherwise exploit any part of
        the Service without our express written consent.
      </p>
      <p>
        By submitting content to the platform (such as venue descriptions, photos or policies),
        you grant Gaamed a non-exclusive, worldwide, royalty-free licence to use, display and
        distribute that content in connection with operating the Service.
      </p>

      <h2 id="ugc-terms">9. User-generated content and reviews</h2>
      <p>
        <strong>Current UGC Terms:</strong>{" "}
        <a href="https://gaamed.com/legal/ugc-terms/ugc-2026-07-17-v1"><code>ugc-2026-07-17-v1</code></a>{" "}· effective July 17, 2026. The canonical UGC rules are maintained on this Terms page; the app links to this artifact before review submission.
      </p>
      <p>
        Reviews are for genuine experiences at a booked venue. Do not post threats, harassment,
        hate, sexual content, personal data, spam, impersonation, or knowingly false or
        manipulative claims. Do not use reviews to arrange private contact or transactions.
      </p>
      <p>
        We may review, limit visibility of, remove, or retain content when needed to enforce
        these rules, respond to a report, comply with law, or protect users. We may also restrict
        accounts that repeatedly misuse reviews. Moderation is not a promise that every review
        is independently verified.
      </p>
      <p>
        You can report review content or a reviewer, and you can hide a reviewer from your own
        view. Reports are reviewed using the information available to us; reviewer-hide is a
        personal visibility control and does not itself remove content for everyone.
      </p>
      <p>
        Before submitting a review, you must explicitly accept the User-generated Content Terms
        shown in the app. If the acceptance check cannot be verified, a review cannot be sent.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF
        ANY KIND, EITHER EXPRESS OR IMPLIED. GAAMED DOES NOT WARRANT THAT THE SERVICE WILL BE
        UNINTERRUPTED, ERROR-FREE OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
      </p>
      <p>
        Gaamed does not endorse, verify or guarantee the quality, safety or legality of any
        venue listed on the platform. Your interactions with venue operators are solely between
        you and the operator.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, GAAMED WILL NOT BE LIABLE FOR ANY INDIRECT,
        INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO
        YOUR USE OF THE SERVICE, EVEN IF GAAMED HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
        DAMAGES.
      </p>
      <p>
        In no event will Gaamed's total liability to you for all claims arising out of or
        related to the Service exceed the greater of (a) the amount you paid to Gaamed in the
        twelve months preceding the claim or (b) USD 100.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to indemnify, defend and hold harmless Gaamed and its officers, directors,
        employees and agents from any claims, liabilities, damages, losses and expenses,
        including reasonable legal fees, arising out of or related to your use of the Service,
        your violation of these Terms, or your violation of any rights of a third party.
      </p>

      <h2>13. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the Arab
        Republic of Egypt, without regard to its conflict of law provisions. Any dispute
        arising under these Terms shall be subject to the exclusive jurisdiction of the courts
        of Cairo, Egypt.
      </p>

      <h2>14. Changes to These Terms</h2>
      <p>
        We reserve the right to modify these Terms at any time. We will notify registered
        users of material changes by email or via a notice on the platform. Your continued use
        of the Service after such notice constitutes acceptance of the updated Terms.
      </p>

      <h2>15. Contact</h2>
      <p>
        If you have any questions about these Terms, please contact us at:{" "}
        <a href="mailto:legal@gaamed.com">legal@gaamed.com</a>
      </p>
    </LegalPage>
  );
}
